import { useEffect } from 'react';
import { Capacitor } from '@capacitor/core';
import { Keyboard, KeyboardResize } from '@capacitor/keyboard';

// Make a full-screen modal's bottom action bar (search / filters) rise glued to
// the iOS keyboard, with no lag.
//
// The lag we're fixing: @capacitor/keyboard's default 'native' resize shrinks
// the WKWebView, but the plugin deliberately delays that shrink by
// (animationDuration + 0.2s) to avoid mid-animation jank (Keyboard.m
// setKeyboardHeight:delay:). A bar that's a flex child of a `fixed inset-0`
// modal only rises once that shrink lands, so it sits behind the keyboard for
// ~0.5s and then pops up.
//
// So while the modal is open we:
//   1. switch the keyboard to 'none' resize (no native shrink to wait on);
//   2. pad the modal root by the live keyboard height — written STRAIGHT to the
//      DOM node from keyboardWillShow (which fires at the start of the keyboard
//      animation), so there's no React re-render latency; a CSS transition on
//      the node animates it in sync;
//   3. focus the input ONLY AFTER (1) and (2) are live, so the very first show
//      is already tracked (otherwise autofocus raises the keyboard before the
//      async listener is registered and the first event is missed).
// 'native' resize is restored on close so the rest of the app keeps its
// automatic input-into-view behaviour.
//
// Args: `ref` → the element to pad; `active` → the modal's `open` flag; optional
// `focusRef` → an input to focus once tracking is live. No-ops on web.
export default function useKeyboardInset(ref, active, focusRef) {
  useEffect(() => {
    if (!active) return undefined;

    const pad = (px) => {
      const el = ref?.current;
      if (el) el.style.paddingBottom = px ? `${px}px` : '';
    };

    if (!Capacitor?.isNativePlatform?.()) {
      focusRef?.current?.focus?.();
      return undefined;
    }

    let handles = [];
    let cancelled = false;
    (async () => {
      await Keyboard.setResizeMode({ mode: KeyboardResize.None }).catch(() => {});
      const sh = await Keyboard.addListener('keyboardWillShow', (info) => pad(info?.keyboardHeight || 0)).catch(() => null);
      const hh = await Keyboard.addListener('keyboardWillHide', () => pad(0)).catch(() => null);
      if (cancelled) { sh?.remove?.(); hh?.remove?.(); return; }
      handles = [sh, hh].filter(Boolean);
      // Listeners live + resize already 'none' → safe to raise the keyboard.
      focusRef?.current?.focus?.();
    })();

    return () => {
      cancelled = true;
      handles.forEach((h) => h?.remove?.());
      Keyboard.setResizeMode({ mode: KeyboardResize.Native }).catch(() => {});
      pad(0);
    };
  }, [active]);
}
